import { zodResolver } from '@hookform/resolvers/zod';
import { type QuoteDocument, type QuoteLine, type QuotePhoto } from '@kalos/kalos-rpc';
import {
  Card,
  Checkbox,
  Form as FormContext,
  FormControl,
  FormField,
  FormItem,
  toast,
} from '@kalos/ui';
import { type FC, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { getLineDiscount } from './discount';
import { type EvidencePhoto, groupEvidencePhotosBySystem } from './photoGroups';

// Schema for review form
const reviewSchema = z.object({
  acceptedServices: z.array(z.number()).min(1, 'Please select at least one service'),
});

type ReviewFormData = z.infer<typeof reviewSchema>;

interface CustomerProposalReviewProps {
  customer: {
    firstname?: string;
    lastname?: string;
    businessname?: string;
    email?: string;
    phone?: string;
  };
  preparedBy: {
    name: string;
    title?: string;
    email: string;
    phone: string;
  };
  companyInfo: {
    name: string;
    address: string;
    phone: string;
    email: string;
    logo?: string;
  };
  property?: {
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  services: QuoteLine[];
  technicalNotes?: string;
  quotePhotos?: QuotePhoto[];
  evidencePhotos?: EvidencePhoto[];
  proposalValidity: {
    validUntil: Date;
    agreementDuration: Date;
  };
  quoteDocument?: QuoteDocument;
  onNext: (data: ReviewFormData) => void;
  onBack?: () => void;
  onServicesChange?: (acceptedServices: number[]) => void;
  initialSelectedServices?: number[];
  discount?: string; // Discount percentage from event
  discountCost?: string; // Flat discount amount from event
  taxRate?: string; // Tax rate percentage from event
}

export const CustomerProposalReview: FC<CustomerProposalReviewProps> = ({
  customer,
  preparedBy,
  companyInfo,
  property,
  services,
  technicalNotes,
  evidencePhotos,
  proposalValidity,
  quoteDocument,
  onNext,
  onServicesChange,
  initialSelectedServices = [],
  discount,
  discountCost,
  taxRate: taxRateProp,
}) => {
  const [enlargedImage, setEnlargedImage] = useState<string | null>(null);

  // Store initial quote statuses to determine which services are already approved
  const [initialQuoteStatuses] = useState<Record<number, number>>(() => {
    return services.reduce(
      (acc, service) => {
        acc[service.id] = service.quoteStatus || 0;
        return acc;
      },
      {} as Record<number, number>,
    );
  });

  const form = useForm<ReviewFormData>({
    resolver: zodResolver(reviewSchema),
    defaultValues: {
      acceptedServices: [
        ...initialSelectedServices,
        // Automatically include already-approved services
        ...services
          .filter((service) => initialQuoteStatuses[service.id] === 1)
          .map((service) => service.id)
          .filter((id) => !initialSelectedServices.includes(id)), // Avoid duplicates
      ],
    },
  });

  // Calculate selected total (includes both selected services AND already-approved services)
  const acceptedServices = form.watch('acceptedServices');
  const selectedSubtotal = services.reduce((sum, service) => {
    const isAlreadyApproved = initialQuoteStatuses[service.id] === 1;
    const isSelected = acceptedServices.includes(service.id);

    // Include service if it's selected OR already approved
    if (isSelected || isAlreadyApproved) {
      const price = parseFloat(service.adjustment || '0');
      const quantity = service.quotedQuantity || 1;
      return sum + price * quantity;
    }
    return sum;
  }, 0);

  // Calculate discount - EITHER flat discount OR percentage discount (flat takes priority)
  const discountPercentValue = parseFloat(discount || '0');
  const discountCostValue = parseFloat(discountCost || '0');
  const discountAmount =
    discountCostValue > 0
      ? discountCostValue
      : services.reduce((sum, service) => {
          const isAlreadyApproved = initialQuoteStatuses[service.id] === 1;
          const isSelected = acceptedServices.includes(service.id);
          if (!isSelected && !isAlreadyApproved) return sum;
          const lineTotal = parseFloat(service.adjustment || '0') * (service.quotedQuantity || 1);
          return (
            sum +
            getLineDiscount(
              lineTotal,
              discountPercentValue,
              discountCostValue,
              service.excludeInvoiceDiscount,
            )
          );
        }, 0);
  const afterDiscount = Math.max(0, selectedSubtotal - discountAmount);

  // Calculate tax
  const parsedTaxRate = parseFloat(taxRateProp || '0') || 0;
  const taxAmount = afterDiscount * (parsedTaxRate / 100);
  const selectedTotal = afterDiscount + taxAmount;

  const today = new Date();
  const daysBetween = (date1: Date, date2: Date) => {
    const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
    const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());
    return Math.max(0, Math.ceil((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24)));
  };
  const proposalValidityDays = daysBetween(today, proposalValidity.validUntil);
  const agreementDurationDays = daysBetween(today, proposalValidity.agreementDuration);

  // Get selectable services (not already approved)
  const selectableServices = services.filter((service) => initialQuoteStatuses[service.id] !== 1);
  const allSelectableSelected = selectableServices.every((service) =>
    acceptedServices.includes(service.id),
  );

  // Select All handler
  const handleSelectAll = useCallback(() => {
    // Select all services (both selectable and already-approved)
    const allServiceIds = services.map((service) => service.id);
    form.setValue('acceptedServices', allServiceIds);
  }, [services, form]);

  // useEffect to notify parent on change
  useEffect(() => {
    if (onServicesChange) {
      onServicesChange(acceptedServices);
    }
  }, [acceptedServices, onServicesChange]);

  const handleSubmit = useCallback(
    (data: ReviewFormData) => {
      if (data.acceptedServices.length === 0) {
        toast({
          title: 'Action Required',
          description: 'Please confirm at least one service before proceeding',
          variant: 'destructive',
        });
        return;
      }

      onNext(data);
    },
    [onNext],
  );

  return (
    <div>
      {/* Top Alert for Review Your Proposal */}
      <div
        className="rounded-lg border border-blue-100 bg-blue-50 p-4"
        style={{
          backgroundColor: 'rgb(239 246 255)',
          borderColor: 'rgb(219 234 254)',
          marginBottom: '2.5rem',
        }}
      >
        <div className="flex items-start gap-3">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-info mt-0.5 h-5 w-5 flex-shrink-0"
            style={{ color: 'rgb(29 78 216)' }}
          >
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M12 16v-4"></path>
            <path d="M12 8h.01"></path>
          </svg>
          <div>
            <h3 className="mb-1 font-medium" style={{ color: 'rgb(29 78 216)' }}>
              Review Your Proposal
            </h3>
            <p className="text-sm" style={{ color: 'rgb(29 78 216)' }}>
              Please review all details below and confirm each service by checking the boxes. Once
              confirmed, click &quot;Next&quot; to sign and complete your proposal.
            </p>
          </div>
        </div>
      </div>
      {/* Client & Preparer Information */}
      <div style={{ paddingBottom: '2.5rem' }}>
        <Card className="bg-white p-6" style={{ marginBottom: '6rem !important' }}>
          <div className="mb-6 flex items-center" style={{ marginBottom: '2rem' }}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-users mr-2 h-6 w-6 text-gray-600"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            <h2 className="text-xl font-semibold">Client & Company Information</h2>
          </div>

          <div className="space-y-8">
            {/* Client Information and Prepared By - Side by Side */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Client Information */}
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <h3 className="mb-3 text-lg font-medium">Client Information</h3>
                <div className="space-y-2">
                  <p className="break-words">
                    <span className="font-medium">Name:</span>{' '}
                    {customer.businessname
                      ? `${customer.businessname} (${customer.firstname} ${customer.lastname})`
                      : `${customer.firstname} ${customer.lastname}`}
                  </p>
                  <p className="break-words">
                    <span className="font-medium">Email:</span> {customer.email}
                  </p>
                  <p className="break-words">
                    <span className="font-medium">Phone:</span> {customer.phone}
                  </p>
                  {property && (
                    <p className="break-words">
                      <span className="font-medium">Site Address:</span>{' '}
                      {[property.address, property.city, property.state, property.zip]
                        .filter(Boolean)
                        .join(', ')}
                    </p>
                  )}
                </div>
              </div>

              {/* Prepared By */}
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <h3 className="mb-3 text-lg font-medium">Prepared By</h3>
                <div className="space-y-2">
                  <p className="break-words">
                    <span className="font-medium">Name:</span> {preparedBy.name}
                  </p>
                  {preparedBy.title && (
                    <p className="break-words">
                      <span className="font-medium">Title:</span> {preparedBy.title}
                    </p>
                  )}
                  <p className="break-words">
                    <span className="font-medium">Email:</span> office@kalosflorida.com
                  </p>
                  <p className="break-words">
                    <span className="font-medium">Phone:</span> (352) 243-7099
                  </p>
                </div>
              </div>
            </div>

            {/* Company Information - Full Width Below */}
            <div
              className="rounded-lg border border-gray-200 bg-gray-50 p-4"
              style={{ marginTop: '1rem' }}
            >
              <h3 className="mb-3 text-lg font-medium">Company Information</h3>
              <div>
                <h4 className="break-words text-lg font-semibold">{companyInfo.name}</h4>
                <div className="mt-2 space-y-1 text-sm">
                  <p className="break-words">{companyInfo.address}</p>
                  <p className="break-words">Phone: {companyInfo.phone}</p>
                  <p className="break-words">Email: {companyInfo.email}</p>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Technical Notes */}
      {technicalNotes && quoteDocument?.includeJobNotes === 1 && (
        <Card className="mb-12 bg-white p-6">
          <h2 className="mb-4 text-xl font-semibold">Technician Notes</h2>
          <div className="whitespace-pre-wrap break-words text-sm">{technicalNotes}</div>
        </Card>
      )}

      {/* Supporting Documentation — grouped by system, above Proposed Solutions */}
      {Array.isArray(evidencePhotos) &&
        evidencePhotos.length > 0 &&
        quoteDocument?.includeAttachments === 1 && (
          <Card className="mb-12 bg-white p-6">
            <h3 className="mb-4 text-xl font-bold">Supporting Documentation</h3>
            {groupEvidencePhotosBySystem(evidencePhotos).map((group) => (
              <div key={group.serviceItemId} className="mb-6 last:mb-0">
                <h4 className="mb-2 text-lg font-semibold">{group.label}</h4>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                  {group.photos.map((photo) => (
                    <div
                      key={photo.id}
                      className="flex items-center justify-center rounded border border-gray-200 p-2 shadow"
                    >
                      <img
                        src={photo.src}
                        alt={group.label}
                        className="h-28 w-full cursor-pointer rounded object-contain transition-transform hover:scale-105"
                        onClick={() => setEnlargedImage(photo.src)}
                        style={{ objectFit: 'contain', background: '#fff', maxHeight: '7rem' }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </Card>
        )}

      {/* Proposed Solutions */}
      <FormContext {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <Card
            className="mb-12 overflow-hidden border-l-4 bg-white p-6"
            style={{ borderLeftColor: 'rgb(59 130 246)', marginTop: '2rem' }}
          >
            <div className="mb-4 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-briefcase mr-2 h-6 w-6"
                style={{ color: 'hsl(var(--primary))' }}
              >
                <path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                <rect width="20" height="14" x="2" y="6" rx="2"></rect>
              </svg>
              <h2 className="text-xl font-semibold">Proposed Solutions</h2>
            </div>
            {form.watch('acceptedServices').length === 0 && (
              <div className="mb-4">
                <div
                  className="flex items-center rounded border px-4 py-2"
                  style={{
                    backgroundColor: 'rgb(255 251 235)',
                    borderColor: 'rgb(253 230 138)',
                    color: 'rgb(146 64 14)',
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-info mr-2 h-5 w-5 flex-shrink-0"
                    style={{ color: 'rgb(146 64 14)' }}
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M12 16v-4"></path>
                    <path d="M12 8h.01"></path>
                  </svg>
                  <span>
                    You must check at least one service to confirm your selection before proceeding.
                  </span>
                </div>
              </div>
            )}
            <div className="mb-4 flex justify-end">
              <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
                <Checkbox
                  checked={allSelectableSelected}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      handleSelectAll();
                    }
                  }}
                  style={{
                    borderColor: '#22c55e',
                    backgroundColor: allSelectableSelected ? '#22c55e' : 'transparent',
                  }}
                  className="rounded text-white"
                />
                <span>Select all Unselected Services</span>
              </label>
            </div>
            <div className="space-y-3">
              {services.map((service) => {
                const price = parseFloat(service.adjustment || '0');
                const quantity = service.quotedQuantity || 1;
                const totalPrice = price * quantity;
                const lineDiscount = getLineDiscount(
                  totalPrice,
                  discountPercentValue,
                  discountCostValue,
                  service.excludeInvoiceDiscount,
                );
                const isAlreadyApproved = initialQuoteStatuses[service.id] === 1;
                return (
                  <div
                    key={service.id}
                    className={`rounded border bg-white p-4 ${isAlreadyApproved ? 'opacity-60' : ''}`}
                  >
                    <div className="mb-3 flex items-start gap-3">
                      <FormField
                        control={form.control}
                        name="acceptedServices"
                        render={({ field }) => (
                          <FormItem className="flex items-center space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value.includes(service.id)}
                                disabled={isAlreadyApproved}
                                onCheckedChange={(checked) => {
                                  const newValue = checked
                                    ? [...field.value, service.id]
                                    : field.value.filter((id) => id !== service.id);
                                  field.onChange(newValue);
                                }}
                                style={{
                                  borderColor: '#22c55e',
                                  backgroundColor: field.value.includes(service.id)
                                    ? '#22c55e'
                                    : 'transparent',
                                }}
                                className="mt-0.5 shrink-0 rounded text-white"
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="whitespace-pre-line break-words text-sm font-medium">
                          {service.description}
                        </div>
                        {isAlreadyApproved && (
                          <div className="mt-1 text-xs font-semibold text-green-600">
                            (Already Approved)
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-6 text-sm">
                      <div>
                        <span className="text-gray-600">Qty:</span>
                        <span className="ml-2 font-medium">{quantity}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Unit Price:</span>
                        <span className="ml-2 font-medium">${price.toFixed(2)}</span>
                      </div>
                      {lineDiscount > 0 && (
                        <div>
                          <span className="font-medium text-green-700">
                            Discount -${lineDiscount.toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-600">Total:</span>
                        <span className="ml-2 font-semibold">${totalPrice.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}

              <div className="space-y-2 rounded border border-gray-300 bg-gray-100 p-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">Subtotal</span>
                  <span className="font-medium">${selectedSubtotal.toFixed(2)}</span>
                </div>
                {(discountCostValue > 0 || discountPercentValue > 0) && discountAmount > 0 && (
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <span>{discountCostValue > 0 ? 'Discount' : 'Total discount'}</span>
                    <span>-${discountAmount.toFixed(2)}</span>
                  </div>
                )}
                {parsedTaxRate > 0 && (
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <span>Tax ({parsedTaxRate}%)</span>
                    <span>${taxAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between border-t pt-2">
                  <span className="text-lg font-bold">Selected Total</span>
                  <span className="text-xl font-bold">${selectedTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </Card>

          {/* Modal for enlarged evidence image (opened from the Supporting Documentation grid above) */}
          {enlargedImage && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80"
              onClick={() => setEnlargedImage(null)}
            >
              <div className="relative" onClick={(e) => e.stopPropagation()}>
                <img
                  src={enlargedImage || ''}
                  alt="Enlarged Evidence"
                  className="max-h-[90vh] max-w-[95vw] rounded border-4 border-white bg-white shadow-lg"
                  style={{ objectFit: 'contain' }}
                />
                <button
                  className="absolute right-2 top-2 rounded-full bg-black bg-opacity-50 px-3 py-1 text-3xl font-bold text-white hover:bg-opacity-80"
                  onClick={() => setEnlargedImage(null)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
            </div>
          )}
          {/* Agreement Timeline */}
          <div style={{ paddingTop: '2.5rem' }}>
            <Card
              className="border-l-4 bg-white p-6"
              style={{ borderLeftColor: 'hsl(var(--primary))', marginTop: '6rem !important' }}
            >
              <div className="mb-4 flex items-center">
                <svg
                  className="mr-2 h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  style={{ color: 'hsl(var(--primary))' }}
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <path d="M16 2v4M8 2v4M3 10h18" />
                </svg>
                <h2 className="text-xl font-semibold">Agreement Timeline</h2>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {/* Proposal Validity */}
                <div className="flex h-full flex-col justify-between rounded-lg border p-4">
                  <div className="mb-2 flex items-center">
                    <svg
                      className="mr-2 h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      style={{ color: 'hsl(var(--primary))' }}
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" />
                      <path d="M16 2v4M8 2v4M3 10h18" />
                    </svg>
                    <span className="font-medium">Proposal Validity</span>
                  </div>
                  <div className="mb-2 flex items-end gap-2">
                    <span className="text-3xl font-bold">{proposalValidityDays}</span>
                    <span className="text-gray-500">days</span>
                  </div>
                  <div
                    className="w-fit rounded px-2 py-1 text-xs font-medium"
                    style={{
                      backgroundColor: 'hsl(var(--primary) / 0.1)',
                      color: 'hsl(var(--primary))',
                    }}
                  >
                    Expires on: {proposalValidity.validUntil.toLocaleDateString()}
                  </div>
                </div>
                {/* Agreement Duration */}
                <div className="flex h-full flex-col justify-between rounded-lg border p-4">
                  <div className="mb-2 flex items-center">
                    <svg
                      className="mr-2 h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      style={{ color: 'hsl(var(--primary))' }}
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M9 12l2 2 4-4" />
                    </svg>
                    <span className="font-medium">Agreement Duration</span>
                  </div>
                  <div className="mb-2 flex items-end gap-2">
                    <span className="text-3xl font-bold">{agreementDurationDays}</span>
                    <span className="text-gray-500">days</span>
                  </div>
                  <div
                    className="w-fit rounded px-2 py-1 text-xs font-medium"
                    style={{
                      backgroundColor: 'hsl(var(--primary) / 0.1)',
                      color: 'hsl(var(--primary))',
                    }}
                  >
                    Ends on: {proposalValidity.agreementDuration.toLocaleDateString()}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </form>
      </FormContext>
    </div>
  );
};
